import * as Sentry from '@sentry/browser'
import SentryRRWeb from '@sentry/rrweb'
import {
  browserVersion,
  engineName,
  engineVersion,
  fullBrowserVersion,
  getUA,
  mobileModel,
  osName,
  osVersion,
} from 'react-device-detect'

import { sentryRavenLink } from 'app/config'
import { loadScript } from 'helpers/loadScriptAsync'
import { store } from '../store'
import { lastAction } from 'store/entities/offline/selectors'

const getSizeInBytes = obj => {
  let str = null
  if (typeof obj === 'string') {
    // If obj is a string, then use it
    str = obj
  } else {
    // Else, make obj into a string
    str = JSON.stringify(obj)
  }
  // Get the length of the Uint8Array
  const bytes = new TextEncoder().encode(str).length
  return bytes
}

export const logSizeInKilobytes = (description, obj) => {
  const bytes = getSizeInBytes(obj)
  const kb = (bytes / 1000).toFixed(2)
  console.log(`${description} is approximately ${kb} kB`)
}

/**
 * Sentry Raven
 * @see https://docs.sentry.io/clients/javascript/integrations/react/
 * @return {Promise<any>}
 */
export function ravenInit() {
  return new Promise((resolve, reject) => {
    try {
      if (sentryRavenLink) {
        loadScript('https://cdn.ravenjs.com/3.26.4/raven.min.js')
          .catch(reject)
          .then(() => {
            const RavenInit = Sentry.init({
              dsn: sentryRavenLink,
              environment: process.env.SERVER || 'dev',
              integrations: [new SentryRRWeb()],
              beforeSend: (exception, dataError) => {
                console.log('__beforeSend__')
                /*
                  extra info about that error https://github.com/getsentry/sentry-javascript/issues/3440
                */
                if (exception?.exception?.values?.length) {
                  const e = exception.exception.values[0]
                  if (
                    e.type === 'UnhandledRejection' &&
                    e.value.includes('Non-Error promise rejection captured with value: Object Not Found Matching Id')
                  ) {
                    return null
                  }
                }
                const st = store.getState()
                const identity = st.get('persist').identity
                exception.user.email = identity.get('email')
                const logStore = st.toJS()
                const extraState = {
                  persist: {},
                  feed: {},
                }
                const persistKeys = Object.keys(logStore.persist)
                // TODO: @serginyo90 need update recent below
                // As application state can be very large and easily exceed the 200kB maximum that Sentry has on individual event payloads.
                // We catch the error "413 Request Entity Too Large" if we save feed
                for (let i = 0; i < persistKeys.length; i++) {
                  const prop = persistKeys[i]
                  if (prop === 'organizations' || prop === '_persist') {
                    continue
                  }
                  if (prop === 'identity') {
                    const identity = logStore.persist[prop].toJS()
                    identity.onboardingLevels = null
                    extraState.persist[prop] = identity
                    continue
                  }
                  extraState.persist[prop] = logStore.persist[prop].toJS()
                }
                extraState.feed.recent = null
                extraState.lastAction = lastAction(st)
                exception.extra.state = extraState
                console.log('__extraState__', extraState)
                exception.extra.dataError = dataError
                if (process.env.NODE_ENV !== 'production') {
                  try {
                    logSizeInKilobytes('exception', exception)
                    // logSizeInKilobytes('exception.extra', exception.extra)
                    // logSizeInKilobytes('exception.extra.state', exception.extra.state)
                    // logSizeInKilobytes('exception.extra.state.activities', exception.extra.state.activities)
                    // logSizeInKilobytes('exception.extra.state.adminWelcomeEmail', exception.extra.state.adminWelcomeEmail)
                    // logSizeInKilobytes('exception.extra.state.analytics', exception.extra.state.analytics)
                    // logSizeInKilobytes('exception.extra.state.companyStats', exception.extra.state.companyStats)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.completedProtracks',
                    //   exception.extra.state.completedProtracks
                    // )
                    // logSizeInKilobytes(
                    //   'exception.extra.state.differentActivities',
                    //   exception.extra.state.differentActivities
                    // )
                    // logSizeInKilobytes('exception.extra.state.feed', exception.extra.state.feed)
                    // logSizeInKilobytes('exception.extra.state.form', exception.extra.state.form)
                    // logSizeInKilobytes('exception.extra.state.gallery', exception.extra.state.gallery)
                    // logSizeInKilobytes('exception.extra.state.globalValues', exception.extra.state.globalValues)
                    // logSizeInKilobytes('exception.extra.state.loadingBar', exception.extra.state.loadingBar)
                    // logSizeInKilobytes('exception.extra.state.modal', exception.extra.state.modal)
                    // logSizeInKilobytes('exception.extra.state.notifications', exception.extra.state.notifications)
                    // logSizeInKilobytes('exception.extra.state.orgHolidays', exception.extra.state.orgHolidays)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.orgInactiveNotifications',
                    //   exception.extra.state.orgInactiveNotifications
                    // )
                    // logSizeInKilobytes('exception.extra.state.orgLabels', exception.extra.state.orgLabels)
                    // logSizeInKilobytes('exception.extra.state.orgValues', exception.extra.state.orgValues)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.organizationSettings',
                    //   exception.extra.state.organizationSettings
                    // )
                    // logSizeInKilobytes('exception.extra.state.persist', exception.extra.state.persist)
                    // logSizeInKilobytes('exception.extra.state.persist._persist', exception.extra.state.persist._persist)
                    // logSizeInKilobytes('exception.extra.state.persist.activity', exception.extra.state.persist.activity)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.persist.companyStats',
                    //   exception.extra.state.persist.companyStats
                    // )
                    // logSizeInKilobytes(
                    //   'exception.extra.state.persist.companyStats',
                    //   exception.extra.state.persist.companyStats
                    // )
                    // logSizeInKilobytes('exception.extra.state.persist.generator', exception.extra.state.persist.generator)
                    // logSizeInKilobytes('exception.extra.state.persist.identity', exception.extra.state.persist.identity)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.persist.localSettings',
                    //   exception.extra.state.persist.localSettings
                    // )
                    // logSizeInKilobytes('exception.extra.state.persist.offline', exception.extra.state.persist.offline)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.persist.onboarding',
                    //   exception.extra.state.persist.onboarding
                    // )
                    // logSizeInKilobytes(
                    //   'exception.extra.state.persist.organizations',
                    //   exception.extra.state.persist.organizations
                    // )
                    // logSizeInKilobytes('exception.extra.state.persist.protracks', exception.extra.state.persist.protracks)
                    // logSizeInKilobytes('exception.extra.state.persist.reviews', exception.extra.state.persist.reviews)
                    // logSizeInKilobytes(
                    //   'exception.extra.state.persist.reviewsSurveys',
                    //   exception.extra.state.persist.reviewsSurveys
                    // )
                    // logSizeInKilobytes('exception.extra.state.persist.users', exception.extra.state.persist.users)
                    // logSizeInKilobytes('exception.extra.state.router', exception.extra.state.router)
                    // logSizeInKilobytes('exception.extra.state.toastr', exception.extra.state.toastr)
                    // logSizeInKilobytes('exception.extra.state.tour', exception.extra.state.tour)
                    // logSizeInKilobytes('exception.extra.state.ui', exception.extra.state.ui)
                    // logSizeInKilobytes('exception.extra.state.videos', exception.extra.state.videos)
                    // logSizeInKilobytes('exception.extra.lastAction', exception.extra.lastAction)
                    // logSizeInKilobytes('exception.extra.dataError', exception.extra.dataError)
                  } catch (e) {
                    console.log('__e__', e)
                    logSizeInKilobytes('exception.extra', exception.extra)
                  }
                }
                return exception
              },
            })
            resolve(RavenInit)
          })
          .then(() => {
            Sentry.configureScope(scope => {
              scope.setUser({ email: 'default@email.com' })
              scope.setExtra('osVersion', osVersion)
              scope.setExtra('osName', osName)
              scope.setExtra('fullBrowserVersion', fullBrowserVersion)
              scope.setExtra('getUA', getUA)
              scope.setExtra('engineVersion', engineVersion)
              scope.setExtra('engineName', engineName)
              scope.setExtra('mobileModel', mobileModel)
              scope.setExtra('browserVersion', browserVersion)
            })
          })
          .catch(e => {
            console.log('__eeee__', e)
          })
      } else {
        reject(new Error('[Sentry Raven]: initialization failed - config:sentryRavenLink is empty'))
      }
    } catch (error) {
      reject(error)
    }
  })
}
